import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  addToast,
} from "@heroui/react";
import { useEffect, useState, useCallback, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import ReviewForm from '../ReviewForm';
import { useReview } from '~/hooks/useReview';
import { useLocale } from '~/hooks/useLocale';
import { ROUTES } from '~/constants/routes.contants';
import { FoodResponseDto } from "@bitecritic-api/src/foods/dto/food.response.dto";
import { ReviewCreateDTO } from "@bitecritic-api/src/reviews/dto/review.dto";
import { reviewSchema } from "~/schemas/reviewSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { now, getLocalTimeZone, ZonedDateTime, parseAbsoluteToLocal } from "@internationalized/date";
import { ImageResponseDto } from "@bitecritic-api/src/images/dto/image.response.dto";
import { GooglePlace } from '@bitecritic-api/src/places/places.controller';
import { useEvent } from "~/hooks/useEvent";
import ImageUpload from '../ImageUpload';
import { motion, AnimatePresence } from "framer-motion";
import { FaImage, FaStar, FaMapMarkerAlt, FaCheck, FaTrash } from 'react-icons/fa';
import { ImagesEntity } from '@bitecritic-api/src/images/images.zod';

type ReviewFormData = Omit<ReviewCreateDTO, 'visitedAt'> & {
  visitedAt: ZonedDateTime;
};

// Type for the draft data stored in localStorage
interface ReviewDraft {
  formData: Partial<ReviewFormData>;
  images: ImageResponseDto[];
  selectedFood: FoodResponseDto | null;
  selectedPlace: GooglePlace | null;
  step: number;
  languageCode: string;
  visitedAtString?: string; // Store ZonedDateTime as string
}

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialFood?: FoodResponseDto | null;
}

const DRAFT_STORAGE_KEY = 'reviewDraft';

// Step animation variants using Framer Motion's custom prop
const pageVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
    transition: { duration: 0.3, ease: 'easeIn' },
  }),
};

// Memoized StepIcon component to prevent unnecessary re-renders
const StepIcon = memo(({ 
  stepNum, 
  isActive, 
  isCompleted, 
  onClick 
}: { 
  stepNum: number; 
  isActive: boolean; 
  isCompleted: boolean;
  onClick: () => void;
}) => {
  const baseClasses = `w-11 h-11 rounded-full flex items-center justify-center cursor-pointer mb-2
    ${isActive 
      ? isCompleted
        ? 'bg-success text-white shadow-lg' 
        : 'bg-primary text-white shadow-lg'
      : isCompleted 
        ? 'bg-success text-white' 
        : 'bg-default-100 text-default-600'
    }
    transition-all duration-300
  `;

  let icon;
  if (isCompleted) {
    icon = <FaCheck size={16} />;
  } else {
    switch (stepNum) {
      case 1: icon = <FaImage size={16} />; break;
      case 2: icon = <FaStar size={16} />; break;
      case 3: icon = <FaMapMarkerAlt size={16} />; break;
      default: icon = null;
    }
  }

  return (
    <motion.div 
      className={baseClasses} 
      onClick={onClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      animate={isActive ? { y: [0, -5, 0], transition: { duration: 0.5 } } : {}}
    >
      {icon}
    </motion.div>
  );
});

StepIcon.displayName = 'StepIcon';

// Memoized StepIndicator component
const StepIndicators = memo(({
  currentStep,
  getStepStatus,
  onStepChange,
  t
}: {
  currentStep: number;
  getStepStatus: (step: number) => boolean;
  onStepChange: (step: number) => void;
  t: (key: string) => string;
}) => {
  // Colors for connectors
  const defaultColor = '#e5e7eb';
  const successColor = '#22c55e';

  return (
    <div className="w-full mb-6 relative">
      {/* Step Connectors */}
      <div className="absolute top-[21px] left-0 w-full z-[-1] flex justify-center">
        <div className="relative w-[180px] h-[2px]">
          <motion.div 
            className="absolute left-0 top-0 h-full"
            style={{ width: '50%' }}
            animate={{
              backgroundColor: currentStep > 1 || getStepStatus(1) ? successColor : defaultColor
            }}
            transition={{ duration: 0.3 }}
          />
          <motion.div 
            className="absolute right-0 top-0 h-full"
            style={{ width: '50%' }}
            animate={{
              backgroundColor: currentStep > 2 || getStepStatus(2) ? successColor : defaultColor
            }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center justify-center space-x-8">
        {[1, 2, 3].map((stepNum) => {
          const isActive = currentStep === stepNum;
          const isCompleted = getStepStatus(stepNum);
          
          return (
            <div key={stepNum} className="flex flex-col items-center gap-1">
              <StepIcon 
                stepNum={stepNum} 
                isActive={isActive} 
                isCompleted={isCompleted} 
                onClick={() => onStepChange(stepNum)}
              />
              <motion.span 
                className={`text-xs ${isActive ? 'text-primary font-medium' : 'text-default-500'}`}
                animate={isActive ? { scale: 1.1 } : { scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {stepNum === 1 ? t('media') : stepNum === 2 ? t('rating') : t('location')}
              </motion.span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

StepIndicators.displayName = 'StepIndicators';

// Memoized Step Content components
const StepContent = memo(({
  step,
  direction,
  isCreating,
  t,
  children
}: {
  step: number;
  direction: number;
  isCreating: boolean;
  t: (key: string) => string;
  children: React.ReactNode;
}) => {
  if (isCreating) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <AnimatePresence initial={false} custom={direction} mode="wait">
      <motion.div
        key={step}
        custom={direction}
        variants={pageVariants}
        initial="enter"
        animate="center"
        exit="exit"
        className="w-full"
      >
        <div className="w-full border-none">
          <div className="p-4">
            {/* Explanation area for each step */}
            <div className="mb-4 rounded-lg px-4 py-3 bg-foreground text-background text-sm font-medium shadow">
              {step === 1 && (t('review-modal-media-explanation'))}
              {step === 2 && (t('review-modal-rating-explanation'))}
              {step === 3 && (t('review-modal-location-explanation'))}
            </div>
            {children}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
});

StepContent.displayName = 'StepContent';

export default function ReviewModal({ isOpen: modalIsOpen, onClose, initialFood }: ReviewModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0); // 1 for forward, -1 for backward
  const { createReview, isCreating, errorMessage } = useReview();
  const { currentLocale, navigateTo } = useLocale();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [languageCode, setLanguageCode] = useState<string>(currentLocale.language);
  const [images, setImages] = useState<ImageResponseDto[]>([]);
  const [selectedFood, setSelectedFood] = useState<FoodResponseDto | null>(initialFood || null);
  const [selectedPlace, setSelectedPlace] = useState<GooglePlace | null>(null);
  const [hasDraft, setHasDraft] = useState(false); // Track if a draft exists
  const { sendEvent } = useEvent();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const draftLoadedRef = useRef<boolean>(false); // Track if draft was loaded in this session
  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    mode: 'onChange',
    defaultValues: {
      type: 'food',
      visitedAt: now(getLocalTimeZone()),
      reviewText: '',
      overallRating: 5,
      cost: 0,
      foodId: initialFood?.id,
      googlePlaceId: '',
      images: [],
    },
  });

  const { watch, handleSubmit, setValue, reset, formState } = form;
  const watchedFields = watch();
  
  // Function to clear draft from localStorage and reset state
  const clearDraftAndReset = useCallback(() => {  
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setHasDraft(false);
    reset({
      type: 'food',
      visitedAt: now(getLocalTimeZone()),
      reviewText: '',
      overallRating: 5,
      cost: 0,
      foodId: initialFood?.id,
      googlePlaceId: '',
      images: [],
    });
    setStep(1);
    setImages([]);
    setSelectedFood(initialFood || null);
    setSelectedPlace(null);
    setLanguageCode(currentLocale.language);
    addToast({
      title: t('draft-cleared'),
      color: 'success',
      timeout: 2000,
    });
    sendEvent('review_draft_cleared');
    draftLoadedRef.current = false; // Reset the draft loaded flag
  }, [initialFood, reset, currentLocale.language, sendEvent, t]);

  // Reset draft loaded flag when modal closes
  useEffect(() => {
    if (!modalIsOpen) {
      draftLoadedRef.current = false;
    } else {
      sendEvent('review_modal_opened');
    }
  }, [modalIsOpen, sendEvent]);
  
  // Load draft from localStorage on initial mount
  useEffect(() => {
    // Only load when modal opens and draft not already loaded
    if (modalIsOpen && !draftLoadedRef.current) {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (savedDraft) {
        try {
          const draft: ReviewDraft = JSON.parse(savedDraft);
          setHasDraft(true);
          
          // Restore form state
          const visitedAtDate = draft.visitedAtString
            ? parseAbsoluteToLocal(draft.visitedAtString)
            : now(getLocalTimeZone());

          reset({ ...draft.formData, visitedAt: visitedAtDate });
          
          // Restore other states
          setImages(draft.images || []);
          setSelectedFood(draft.selectedFood || null);
          setSelectedPlace(draft.selectedPlace || null);
          setStep(draft.step || 1);
          setLanguageCode(draft.languageCode || currentLocale.language);
          
          // Send event only once per modal session
          sendEvent('review_draft_loaded');
          
          // Mark that we've loaded the draft for this session
          draftLoadedRef.current = true;
        } catch (error) {
          console.error('Error loading review draft:', error);
          localStorage.removeItem(DRAFT_STORAGE_KEY); // Clear invalid draft
          setHasDraft(false);
        }
      } else {
        // If no draft, ensure initialFood is set if provided
        if (initialFood) {
          setSelectedFood(initialFood);
          setValue('foodId', initialFood.id);
        }
        setHasDraft(false);
        draftLoadedRef.current = true; // Mark that we've checked for draft this session
      }
    }
  }, [modalIsOpen, reset, setValue, initialFood, currentLocale.language, sendEvent, t]);  // Removed sendEvent from dependencies

  // Debounced function to save draft to localStorage
  const saveDraft = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
       // Only save if the modal is open and form is dirty or images/place/food selected
       if (modalIsOpen && (formState.isDirty || images.length > 0 || selectedPlace || selectedFood)) {
        const draft: ReviewDraft = {
          formData: watchedFields,
          images,
          selectedFood,
          selectedPlace,
          step,
          languageCode,
          visitedAtString: watchedFields.visitedAt?.toAbsoluteString(),
        };
        
        try {
          localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
          setHasDraft(true);
          sendEvent('review_draft_saved');
        } catch (error) {
          console.error('Error saving review draft:', error);
          addToast({
            title: t('draft-save-error'),
            color: 'danger',
            timeout: 3000,
          });
        }
      }
    }, 1000); // Debounce duration: 1 second
  }, [watchedFields, images, selectedFood, selectedPlace, step, languageCode, formState.isDirty, modalIsOpen, t, sendEvent]);

  // Watch relevant states and trigger debounced saveDraft
  useEffect(() => {
    saveDraft();
    
    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [watchedFields, images, selectedFood, selectedPlace, step, languageCode, saveDraft]);

  const handleStepChange = useCallback((newStep: number) => {
    setDirection(newStep > step ? 1 : -1);
    setStep(newStep);
  }, [step]);

  // Memoize validation check functions to prevent unnecessary recalculations
  const isStep1Valid = images.length > 0;
  
  const isStep2Valid = watchedFields.overallRating &&
    watchedFields.foodId && 
    watchedFields.reviewText?.length >= 80 &&
    watchedFields.cost > 0;
    
  const isStep3Valid = watchedFields.googlePlaceId &&
    watchedFields.visitedAt;

  const canSubmit = isStep1Valid && isStep2Valid && isStep3Valid;

  const handleReviewSubmit = useCallback(async (data: ReviewFormData) => {
    try {
      sendEvent('review_submit');
      setIsSubmitting(true);

      // Make sure the form is valid before proceeding
      if (!canSubmit) {
        setIsSubmitting(false);
        return;
      }

      const review = await createReview({
        ...data,
        visitedAt: data.visitedAt.toDate(),
        images: images.map(img => img.id),
        languageCode,
        type: 'food',
      });

      sendEvent('review_submit_success');

      addToast({
        title: t('review.success'),
        color: "success",
        timeout: 3000,
      });

      // Clear draft *before* closing and resetting
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      setHasDraft(false);

      onClose(); // Close modal first

      // Reset form state after modal is closed and draft is cleared
      setTimeout(() => {
        // Reset using default values, not potentially draft values
        reset({
          type: 'food',
          visitedAt: now(getLocalTimeZone()),
          reviewText: '',
          overallRating: 5,
          cost: 0,
          foodId: initialFood?.id, // Reset to potential initial food
          googlePlaceId: '',
          images: [],
        });
        setStep(1);
        setImages([]);
        setSelectedFood(initialFood || null); // Reset to potential initial food
        setSelectedPlace(null);
        setLanguageCode(languageCode);
        // Navigate to new review
        navigateTo(`${ROUTES.REVIEWS}/${review.id}`);
        setIsSubmitting(false); // Ensure submitting state is reset
      }, 300); // Wait for modal close animation
    } catch (error) {
      sendEvent('review_submit_error');
      addToast({
        title: errorMessage || t('review.error'),
        color: "danger",
        timeout: 3000,
      });
      console.error('Error submitting review:', error);
      setIsSubmitting(false);
    }
  }, [canSubmit, createReview, errorMessage, images, initialFood, languageCode, navigateTo, onClose, reset, sendEvent, t]);

  const handleModalClose = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Do not clear draft on manual close
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleFoodSelect = useCallback((food: FoodResponseDto | null) => {
    sendEvent('review_food_select', {
      foodId: food?.id,
    });
    setSelectedFood(food);
    setValue('foodId', food?.id);
  }, [sendEvent, setValue]);

  const handlePlaceSelect = useCallback((place: GooglePlace | null) => {
    sendEvent('review_place_select', {
      placeId: place?.id,
    });
    setSelectedPlace(place);
    setValue('googlePlaceId', place?.id || '');
  }, [sendEvent, setValue]);

  // Check validation status for each step to show visual indicators
  const getStepStatus = useCallback((stepNum: number): boolean => {
    if (stepNum === 1) return !!images.length;
    if (stepNum === 2) return !!(watchedFields.overallRating &&
      watchedFields.foodId && 
      watchedFields.reviewText?.length >= 80 &&
      watchedFields.cost > 0);
    if (stepNum === 3) return !!(watchedFields.googlePlaceId &&
      watchedFields.visitedAt);
    return false;
  }, [images.length, watchedFields.overallRating, watchedFields.foodId, 
      watchedFields.reviewText, watchedFields.cost, watchedFields.googlePlaceId, 
      watchedFields.visitedAt]);

  return (
    <Modal
      isOpen={modalIsOpen}
      onClose={handleModalClose}
      backdrop="blur"
      isDismissable={!isSubmitting}
      placement="top-center"
      shouldBlockScroll={true}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      size="3xl"
      className="min-h-[80vh]"
      classNames={{
        base: "bg-background",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-row items-center gap-2 border-b border-secondary-100">
          <span id="modal-title" className="text-foreground-900 font-semibold">{t('add-food-review')}</span>
          {hasDraft && (
             <Button
                size="sm"
                variant="light"
                color="danger"
                onPress={clearDraftAndReset}
                isDisabled={isSubmitting}
                startContent={<FaTrash size={12} />}
             >
                {t('clear-draft')}
             </Button>
          )}
        </ModalHeader>
        
        <ModalBody className="p-0 overflow-x-hidden">
          <div className="max-w-4xl w-full mx-auto">
            <StepContent step={step} direction={direction} isCreating={isCreating} t={t}>
              {step === 1 && (
                <div className="flex flex-col gap-4">
                  <ImageUpload
                    type="review"
                    multiple
                    onImagesChange={(newImages) => {
                      setImages(newImages as unknown as ImageResponseDto[]);
                    }}
                    existingImages={images as unknown as ImagesEntity[]}
                  />
                </div>
              )}
              {(step === 2 || step === 3) && (
                <form onSubmit={handleSubmit(handleReviewSubmit)}>
                  <ReviewForm
                    form={form}
                    reviewType="food"
                    isSubmitting={isSubmitting}
                    initialFood={selectedFood}
                    initialPlace={selectedPlace}
                    setLanguageCode={setLanguageCode}
                    setImages={setImages}
                    step={step}
                    languageCode={languageCode}
                    images={images}
                    onFoodSelect={handleFoodSelect}
                    onPlaceSelect={handlePlaceSelect}
                  />
                </form>
              )}
            </StepContent>
          </div>
        </ModalBody>
        
        <ModalFooter className="flex flex-col border-t border-secondary-100">
          <StepIndicators 
            currentStep={step} 
            getStepStatus={getStepStatus} 
            onStepChange={handleStepChange}
            t={t}
          />
          
          <div className="flex justify-between w-full">
            {step > 1 ? (
              <Button
                variant="light"
                onPress={() => handleStepChange(step - 1)}
                isDisabled={isSubmitting}
                color="secondary"
              >
                {t('previous')}
              </Button>
            ) : (
              <div></div>
            )}
            
            {step < 3 ? (
              <Button
                color="primary"
                onPress={() => handleStepChange(step + 1)}
                isDisabled={isSubmitting}
                className="font-medium shadow-md hover:shadow-primary-500/40 transition-all duration-300"
              >
                {t('next')}
              </Button>
            ) : (
              <Button
                color="success"
                isDisabled={!canSubmit || isSubmitting}
                isLoading={isSubmitting}
                onPress={() => handleSubmit(handleReviewSubmit)()}
                className="font-medium shadow-md hover:shadow-success-500/40 transition-all duration-300"
              >
                {t('submit-review')}
              </Button>
            )}
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
